import type { DocumentEmailSend, EmailLogEntry } from '@morales/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../api';
import { whenAt } from '../format';
import { useDebounced } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { ErrorBox } from './ui';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Email an invoice or a statement (decision 93): to the contacts that get invoices, or an address typed here, with a
 * note. A test goes only to the addresses typed (staff only, marked TEST), so it works while email to customers is off
 * and never reaches a customer. Below, every email of this document: to whom, when, whether it went, whether the
 * customer opened it, and a button to stop its link.
 */
export function EmailPanel({ target, customerId, preview = false, historyOnly = false, onCompose }: {
  target: { kind: 'invoice'; id: string } | { kind: 'statement'; customerId: string };
  customerId: string;
  /** the invoice's email beside the form, as the customer will see it (decision 171) */
  preview?: boolean;
  /** only the emails already sent, with a button that opens the form */
  historyOnly?: boolean;
  onCompose?: () => void;
}) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: qk.emailStatus, queryFn: ({ signal }) => api.emailStatus(signal), staleTime: 60_000 });
  const customer = useQuery({ queryKey: qk.customer(customerId), queryFn: ({ signal }) => api.customer(customerId, signal) });
  const historyKey = target.kind === 'invoice' ? qk.invoiceEmails(target.id) : qk.customerEmails(customerId);
  const history = useQuery({
    queryKey: historyKey,
    queryFn: ({ signal }) => (target.kind === 'invoice' ? api.invoiceEmails(target.id, signal) : api.customerEmails(customerId, signal)),
  });
  const contacts = (customer.data?.contacts ?? []).filter((c) => c.email);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [typed, setTyped] = useState('');
  const [message, setMessage] = useState('');
  // the email already says "Your invoice is available"; a note of the office's own goes under it
  const note = useDebounced(message.trim(), 400);
  const invoiceId = target.kind === 'invoice' ? target.id : null;
  const look = useQuery({
    // until email to customers is on, only a test can go: the preview shows its [TEST] subject
    queryKey: ['emailPreview', invoiceId, note, !status.data?.live],
    queryFn: ({ signal }) => api.emailPreview(invoiceId!, { ...(note ? { message: note } : {}), test: !status.data?.live }, signal),
    // asked once the note has settled: never a preview of a note about to change
    enabled: preview && !!invoiceId && status.data !== undefined && note === message.trim(),
    placeholderData: keepPreviousData,
  });
  const [done, setDone] = useState<string | null>(null);
  // the contacts accounts payable reads are ticked to begin with
  useEffect(() => {
    if (customer.data) setPicked(Object.fromEntries(customer.data.contacts.filter((c) => c.email).map((c) => [c.email!, c.getsInvoices])));
  }, [customer.data]);
  const extra = typed.split(/[,;\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
  const badTyped = extra.filter((x) => !EMAIL.test(x));
  const typedOk = [...new Set(extra.filter((x) => EMAIL.test(x)))];
  const to = [...new Set([...Object.entries(picked).filter(([, on]) => on).map(([e]) => e.toLowerCase()), ...typedOk])];
  const refreshHistory = () => {
    void qc.invalidateQueries({ queryKey: historyKey });
    // the statement's list holds the invoices' emails too
    void qc.invalidateQueries({ queryKey: qk.customerEmails(customerId) });
  };
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const send = useMutation({
    mutationFn: (body: DocumentEmailSend) => {
      const serialized = JSON.stringify(body);
      if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
      return api.emailDocument(target, body, attempt.current.key);
    },
    onSuccess: (entry: EmailLogEntry) => {
      attempt.current = null;
      setDone(entry.status === 'unknown' ? t('emailStatusUnknown') : entry.test ? t('emailTestSent') : t('emailSent'));
    },
    onError: (err) => {
      // an earlier try that did not finish: once the office has looked, a new tap is a new send (never on "still
      // being sent": that one may yet go)
      if (err instanceof ApiError && err.problem.errors?.some((e) => e.path === 'stale')) attempt.current = null;
    },
    onSettled: refreshHistory,
  });
  const revoke = useMutation({ mutationFn: (linkId: string) => api.revokeDocumentLink(linkId), onSettled: refreshHistory });
  const s = status.data;
  const live = !!s?.live;
  const go = (test: boolean) => {
    setDone(null);
    send.mutate({ to: test ? typedOk : to, test, ...(message.trim() ? { message: message.trim() } : {}) });
  };

  const form = (
    <>
      {s && !s.configured ? (
        <p className="muted">
          {t('emailNotSetUp')} <Link to="/settings/email">{t('emailSettingsTitle')}</Link>
        </p>
      ) : (
        <>
          {!live ? <p className="warn-band">{t('emailOffNote')}</p> : null}
          {contacts.length ? (
            <fieldset className="choice">
              <legend className="label">{t('emailTo')}</legend>
              {contacts.map((c) => (
                <label key={c.id} className="check">
                  <input type="checkbox" checked={!!picked[c.email!]} onChange={(e) => setPicked((cur) => ({ ...cur, [c.email!]: e.target.checked }))} /> {c.name ?? ''} · {c.email}
                </label>
              ))}
            </fieldset>
          ) : (
            <p className="muted">{t('emailNoContacts')}</p>
          )}
          <label className="field">
            <span>{t('emailOther')}</span>
            <input type="text" inputMode="email" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="name@example.com" aria-invalid={badTyped.length > 0 || undefined} />
          </label>
          {badTyped.length ? <p className="field-error">{t('emailBadAddress').replace('{list}', badTyped.join(', '))}</p> : null}
          <label className="field">
            <span>{t('emailMessage')}</span>
            <textarea rows={3} maxLength={2000} value={message} placeholder={preview ? t('emailNotePlaceholder') : undefined} onChange={(e) => setMessage(e.target.value)} />
          </label>
          <div className="row-actions">
            <button type="button" className="btn btn-secondary" disabled={send.isPending || typedOk.length === 0 || badTyped.length > 0} onClick={() => go(true)} title={t('emailTestHint')}>
              {t('emailSendTest')}
            </button>
            <button type="button" className="btn btn-primary" disabled={!live || look.data?.testInvoice === true || send.isPending || to.length === 0 || badTyped.length > 0} onClick={() => go(false)} title={live ? undefined : t('emailOffNote')}>
              {send.isPending ? t('sending') : t('emailSend').replace('{n}', String(to.length))}
            </button>
          </div>
          <p className="muted">{t('emailTestHint')}</p>
          {send.error ? <ErrorBox error={send.error} /> : null}
          {done ? (
            <p className="note" role="status">
              {done}
            </p>
          ) : null}
        </>
      )}
    </>
  );
  const sent = (
    <>
      {revoke.error ? <ErrorBox error={revoke.error} /> : null}
      {history.data?.length ? (
        <ul className="list compact" data-testid="email-history">
          {history.data.map((h) => (
            <li key={h.id} className="row">
              <span className="row-main">
                <span className="row-title">
                  {whenAt(h.createdAt, intlLocale)} · {h.to.join(', ')}
                  {h.test ? <span className="badge"> {t('emailTestBadge')}</span> : null}
                </span>
                <span className={h.status === 'failed' || h.status === 'unknown' ? 'row-sub bad-text' : 'row-sub'}>
                  {h.status === 'sent' ? t('emailStatusSent') : h.status === 'failed' ? `${t('emailStatusFailed')}: ${h.error ?? ''}` : h.status === 'unknown' ? t('emailStatusUnknown') : t('sending')}
                  {h.openedAt ? ` · ${t('emailOpened').replace('{when}', whenAt(h.openedAt, intlLocale)).replace('{n}', String(h.openCount))}` : h.status === 'sent' ? ` · ${t('emailNotOpened')}` : ''}
                  {h.revokedAt ? ` · ${t('emailRevoked')}` : ''}
                </span>
              </span>
              {h.linkId && !h.revokedAt && h.status !== 'failed' ? (
                <button type="button" className="btn btn-ghost btn-sm" disabled={revoke.isPending} onClick={() => { if (window.confirm(`${t('emailRevoke')}: ${t('confirmSure')}`)) revoke.mutate(h.linkId!); }}>
                  {t('emailRevoke')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );

  if (historyOnly) {
    return (
      <section className="card stack-sm no-print" data-testid="email-panel">
        <h2>{target.kind === 'invoice' ? t('emailInvoice') : t('emailStatement')}</h2>
        <div className="row-actions">
          <button type="button" className="btn btn-secondary" onClick={onCompose}>
            {t('emailCompose')}
          </button>
        </div>
        {sent}
      </section>
    );
  }
  if (preview) {
    const p = look.data;
    return (
      <div className="email-compose no-print" data-testid="email-panel">
        <div className="stack-sm">
          {form}
          {sent}
        </div>
        <div className="email-preview" data-testid="email-preview">
          <span className="label">{t('emailPreviewTitle')}</span>
          {p ? (
            <>
              <span className="email-subject">
                <span className="muted">{t('emailSubject')}:</span> <strong>{p.subject}</strong>
              </span>
              {p.payOff ? <p className="muted">{t(`emailPayOff_${p.payOff}`)}</p> : null}
              {/* sandboxed: nothing in it runs; its links aim at a new window, which the sandbox never opens, so a
                  click on the button leaves the preview as it is */}
              <iframe title={t('emailPreviewTitle')} sandbox="" srcDoc={p.html.replace('<head>', '<head><base target="_blank">')} />
            </>
          ) : look.error ? (
            <ErrorBox error={look.error} />
          ) : (
            <p className="muted">{t('loading')}</p>
          )}
        </div>
      </div>
    );
  }
  return (
    <section className="card stack-sm no-print" data-testid="email-panel">
      <h2>{target.kind === 'invoice' ? t('emailInvoice') : t('emailStatement')}</h2>
      {form}
      {sent}
    </section>
  );
}
